import { useEffect } from "react";

type JsonLdValue = Record<string, unknown> | Record<string, unknown>[];

interface SEOProps {
  title: string;
  description: string;
  canonical: string; // path like "/" or "/about"
  ogImage?: string; // absolute URL
  noindex?: boolean;
  jsonLd?: JsonLdValue;
}

const SITE_ORIGIN = "https://acciongroup.com";
const DEFAULT_OG = `${SITE_ORIGIN}/og-image.png`;

const upsertMeta = (
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string,
) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG,
  noindex = false,
  jsonLd,
}: SEOProps) => {
  useEffect(() => {
    const canonicalUrl = canonical.startsWith("http")
      ? canonical
      : `${SITE_ORIGIN}${canonical.startsWith("/") ? "" : "/"}${canonical}`;

    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertLink("canonical", canonicalUrl);

    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex
        ? "noindex,nofollow"
        : "index,follow,max-image-preview:large,max-snippet:-1",
    );

    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description,
    );
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    upsertMeta(
      'meta[property="og:image:secure_url"]',
      "property",
      "og:image:secure_url",
      ogImage,
    );

    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description,
    );
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    // JSON-LD: tag script with data-seo so we replace cleanly per route.
    document
      .querySelectorAll('script[type="application/ld+json"][data-seo="page"]')
      .forEach((n) => n.remove());

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, canonical, ogImage, noindex, jsonLd]);

  return null;
};

export default SEO;
